/**
 * Pure helpers for the Feature flags page, its edit dialog and the vendor bulk actions.
 * Everything here mirrors a rule the identity backend enforces (`FeatureFlagValidator` /
 * `FeatureFlagAuthorization`, on top of the shared evaluation in `./evaluation`); the console
 * only gates UX with them, the backend is the authority.
 */
import dayjs from "dayjs";
import type {
  FeatureFlagCommand,
  FeatureFlagCreateInput,
  FeatureFlagUpdateInput,
} from "@/api/featureFlags";
import type { AppPlatform } from "@/firestore/appVersionPolicies";
import type { FeatureFlagDoc, FeatureFlagMode } from "@/firestore/featureFlags";
import { formatTimestamp } from "@/lib/datetime";
import { PLATFORMS, PLATFORM_LABELS, parseBuild } from "../app-versions/presentation";
import {
  classifyFeatureFlagChange,
  defaultTargeting,
  requiresSuperAdmin,
  type FeatureFlagChange,
  type FeatureFlagSnapshot,
  type FeatureFlagTargeting,
} from "./evaluation";
import {
  FEATURE_FLAG_REGISTRY,
  isFeatureFlagKey,
  keysForVendor,
  VENDOR_LABELS,
  type FeatureFlagKey,
  type FeatureFlagRiskyDirection,
  type FeatureFlagVendor,
} from "./registry";

// Same bounds as the app version policy validator: builds are integers in [0, 10^9], the user
// message is at most 280 characters and the audit reason 1–500 (after sanitization).
export {
  BUILD_FIELD_LABELS,
  MAX_BUILD,
  MAX_MESSAGE_LENGTH,
  MAX_REASON_LENGTH,
  PLATFORMS,
  PLATFORM_LABELS,
  latestUpdatedAt,
  parseBuild,
  validMessage,
  validReason,
} from "../app-versions/presentation";

/** `expectedBackAt` may be at most this far ahead (identity validator). */
export const MAX_EXPECTED_BACK_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

/** One platform's targeting as the console writes it (a known mode, bounds only with `builds`). */
export interface PlatformTargeting {
  mode: FeatureFlagMode;
  min: number | null;
  max: number | null;
}

export type TargetingValues = Record<AppPlatform, PlatformTargeting>;

/** A complete, valid set of values for one flag row, ready to diff against the current row. */
export interface FeatureFlagWrite extends FeatureFlagSnapshot {
  iosMode: FeatureFlagMode;
  androidMode: FeatureFlagMode;
}

/** The targeting half of a {@link FeatureFlagWrite}. */
export type WriteTargeting = Omit<FeatureFlagWrite, "message" | "expectedBackAt">;

export const MODE_OPTIONS: { value: FeatureFlagMode; label: string }[] = [
  { value: "on", label: "On — every build" },
  { value: "off", label: "Off — no build" },
  { value: "builds", label: "Only builds in a range" },
];

// ───────────────────────────── form resolution ─────────────────────────────

/** The dialog's raw inputs for one platform (Mantine `NumberInput` yields `number | ""`). */
export interface PlatformForm {
  mode: FeatureFlagMode;
  min: number | string;
  max: number | string;
}

export interface PlatformResolution {
  value: PlatformTargeting | null;
  minError: string | null;
  maxError: string | null;
  rangeError: string | null;
}

const BOUND_ERROR = "Enter a whole number from 0 to 1 000 000 000, or leave blank.";

function resolveBound(value: number | string): { ok: boolean; value: number | null } {
  if (value === "" || (typeof value === "string" && !value.trim()))
    return { ok: true, value: null };
  const parsed = parseBuild(value);
  return parsed === null ? { ok: false, value: null } : { ok: true, value: parsed };
}

/**
 * Validates one platform's inputs like the backend: bounds only with `builds`, at least one bound,
 * integers in [0, 10^9], min ≤ max. Bounds typed while another mode is selected are dropped.
 */
export function resolvePlatform(form: PlatformForm): PlatformResolution {
  if (form.mode !== "builds") {
    return {
      value: { mode: form.mode, min: null, max: null },
      minError: null,
      maxError: null,
      rangeError: null,
    };
  }
  const min = resolveBound(form.min);
  const max = resolveBound(form.max);
  const minError = min.ok ? null : BOUND_ERROR;
  const maxError = max.ok ? null : BOUND_ERROR;
  let rangeError: string | null = null;
  if (min.ok && max.ok) {
    if (min.value === null && max.value === null) {
      rangeError = "Set a minimum build, a maximum build, or both.";
    } else if (min.value !== null && max.value !== null && min.value > max.value) {
      rangeError = "The minimum build must not exceed the maximum build.";
    }
  }
  const valid = minError === null && maxError === null && rangeError === null;
  return {
    value: valid ? { mode: "builds", min: min.value, max: max.value } : null,
    minError,
    maxError,
    rangeError,
  };
}

export type ResolvedBackAt =
  | { kind: "value"; value: string | null }
  | { kind: "invalid"; reason: string };

/** The `datetime-local` input → the `expectedBackAt` to send. Blank clears it. */
export function resolveExpectedBackAt(input: string, now: number): ResolvedBackAt {
  if (!input.trim()) return { kind: "value", value: null };
  const parsed = Date.parse(input);
  if (!Number.isFinite(parsed)) return { kind: "invalid", reason: "Enter a valid date and time." };
  if (parsed > now + MAX_EXPECTED_BACK_DAYS * DAY_MS) {
    return { kind: "invalid", reason: `At most ${MAX_EXPECTED_BACK_DAYS} days ahead.` };
  }
  return { kind: "value", value: new Date(parsed).toISOString() };
}

/** An ISO instant as a `datetime-local` value in the operator's time zone (blank for null). */
export function toDateTimeLocal(value: string | null): string {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DDTHH:mm") : "";
}

export function sameInstant(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return a === b;
  return Date.parse(a) === Date.parse(b);
}

// ───────────────────────────── current state ─────────────────────────────

function platformOf(t: FeatureFlagTargeting, platform: AppPlatform) {
  return platform === "ios"
    ? { mode: t.iosMode, min: t.iosMinBuild, max: t.iosMaxBuild }
    : { mode: t.androidMode, min: t.androidMinBuild, max: t.androidMaxBuild };
}

/** The evaluation inputs of a row, or the code-default equivalent when the key has no row. */
export function currentSnapshot(
  doc: FeatureFlagSnapshot | null,
  defaultAvailable: boolean,
): FeatureFlagSnapshot {
  if (!doc) return { ...defaultTargeting(defaultAvailable), message: null, expectedBackAt: null };
  return {
    iosMode: doc.iosMode,
    iosMinBuild: doc.iosMinBuild,
    iosMaxBuild: doc.iosMaxBuild,
    androidMode: doc.androidMode,
    androidMinBuild: doc.androidMinBuild,
    androidMaxBuild: doc.androidMaxBuild,
    message: doc.message,
    expectedBackAt: doc.expectedBackAt,
  };
}

/**
 * What users see while the key has no row: the server default, or the apps' own default where it
 * differs and is the same on every bundle (`emailPasswordSignIn`). A create starts from — and is
 * summarised against — this; the superAdmin rule still classifies against the server default.
 */
export function noRowSnapshot(key: FeatureFlagKey): FeatureFlagSnapshot {
  const entry = FEATURE_FLAG_REGISTRY[key];
  return currentSnapshot(null, entry.appDefault?.available ?? entry.defaultAvailable);
}

export function toTargeting(values: TargetingValues): WriteTargeting {
  return {
    iosMode: values.ios.mode,
    iosMinBuild: values.ios.min,
    iosMaxBuild: values.ios.max,
    androidMode: values.android.mode,
    androidMinBuild: values.android.min,
    androidMaxBuild: values.android.max,
  };
}

/** The live row for a key (newest `updatedAt` wins if the collection ever holds two). */
export function liveFlagFor<T extends Pick<FeatureFlagDoc, "key" | "updatedAt" | "deletedAt">>(
  docs: readonly T[],
  key: string,
): T | null {
  let best: T | null = null;
  for (const doc of docs) {
    if (doc.key !== key || doc.deletedAt) continue;
    if (!best || Date.parse(doc.updatedAt) > Date.parse(best.updatedAt)) best = doc;
  }
  return best;
}

/** Live rows whose key this console's registry does not know (the console needs an update). */
export function unknownKeys(docs: readonly Pick<FeatureFlagDoc, "key" | "deletedAt">[]): string[] {
  const keys = new Set<string>();
  for (const doc of docs) if (!doc.deletedAt && !isFeatureFlagKey(doc.key)) keys.add(doc.key);
  return [...keys].sort();
}

/** Past `expectedBackAt` while some platform is still not fully `on`. */
export function isOverdue(
  doc: Pick<FeatureFlagDoc, "iosMode" | "androidMode" | "expectedBackAt"> | null,
  now: number,
): boolean {
  if (!doc?.expectedBackAt) return false;
  const back = Date.parse(doc.expectedBackAt);
  return Number.isFinite(back) && back < now && !(doc.iosMode === "on" && doc.androidMode === "on");
}

/** Some platform is limited to a build range (e.g. a review build). */
export function targetsBuilds(
  doc: Pick<FeatureFlagDoc, "iosMode" | "androidMode"> | null,
): boolean {
  return doc?.iosMode === "builds" || doc?.androidMode === "builds";
}

// ───────────────────────────── copy ─────────────────────────────

function rangeText(min: number | null, max: number | null): string {
  if (min !== null && max !== null) {
    if (min === max) return `build ${min} only`;
    return min > max ? `builds ${min}–${max} (none)` : `builds ${min}–${max}`;
  }
  if (min !== null) return `builds ≥ ${min}`;
  if (max !== null) return `builds ≤ ${max}`;
  return "builds (unbounded)";
}

/** Lower-case targeting text for change summaries, e.g. "on", "off", "build 640 only". */
export function describeTargeting(mode: string, min: number | null, max: number | null): string {
  if (mode === "on" || mode === "off") return mode;
  if (mode === "builds") return rangeText(min, max);
  return `unknown mode "${mode}" (treated as off)`;
}

/** One platform's targeting as change-summary text. */
function platformText(t: FeatureFlagTargeting, platform: AppPlatform): string {
  const { mode, min, max } = platformOf(t, platform);
  return describeTargeting(mode, min, max);
}

export interface PlatformStateBadge {
  label: string;
  color: string;
}

/** Table badge for one platform of a row. */
export function platformStateBadge(
  t: FeatureFlagTargeting,
  platform: AppPlatform,
): PlatformStateBadge {
  const { mode, min, max } = platformOf(t, platform);
  if (mode === "on") return { label: "On", color: "teal" };
  if (mode === "off") return { label: "Off", color: "red" };
  if (mode === "builds") {
    const text = rangeText(min, max);
    return { label: text.charAt(0).toUpperCase() + text.slice(1), color: "yellow" };
  }
  return { label: `Unknown "${mode}" (off)`, color: "red" };
}

/**
 * One-line delta the operator confirms, e.g. "kyc: iOS on → off, Android on → off, message set.".
 * For a create, `before` is {@link noRowSnapshot} (what users see with no row).
 */
export function describeChange(
  key: string,
  before: FeatureFlagSnapshot,
  after: FeatureFlagSnapshot,
  creating: boolean,
): string {
  const parts: string[] = [];
  for (const platform of PLATFORMS) {
    const b = platformOf(before, platform);
    const a = platformOf(after, platform);
    if (b.mode !== a.mode || b.min !== a.min || b.max !== a.max) {
      parts.push(
        `${PLATFORM_LABELS[platform]} ${platformText(before, platform)} → ${platformText(after, platform)}`,
      );
    }
  }
  if ((before.message ?? null) !== (after.message ?? null)) {
    parts.push(
      after.message === null
        ? "message cleared"
        : before.message === null
          ? "message set"
          : "message changed",
    );
  }
  if (!sameInstant(before.expectedBackAt, after.expectedBackAt)) {
    parts.push(
      `expected back ${before.expectedBackAt ? formatTimestamp(before.expectedBackAt) : "none"} → ${after.expectedBackAt ? formatTimestamp(after.expectedBackAt) : "none"}`,
    );
  }
  if (creating) {
    if (parts.length > 0) return `Create the ${key} row: ${parts.join(", ")}.`;
    const appDefault = isFeatureFlagKey(key) ? FEATURE_FLAG_REGISTRY[key].appDefault : null;
    if (!appDefault) {
      return `Create the ${key} row with the code-default targeting (no change for users).`;
    }
    if (appDefault.available !== null) {
      return `Create the ${key} row with the apps' default targeting (no change for users).`;
    }
    // A bundle-dependent app default has no equivalent row: any row changes some bundle.
    const targeting = PLATFORMS.map(
      (platform) => `${PLATFORM_LABELS[platform]} ${platformText(after, platform)}`,
    );
    return `Create the ${key} row: ${targeting.join(", ")} on every bundle, replacing the apps' default (${appDefault.label}).`;
  }
  return parts.length === 0 ? `No change to ${key}.` : `${key}: ${parts.join(", ")}.`;
}

/** What a classified change does to availability, or null when it only edits the copy. */
export function changeEffect(change: FeatureFlagChange): string | null {
  if (change.disables && change.enables) return "Takes some builds away and adds others.";
  if (change.disables) return "Turns the feature off for some builds.";
  if (change.enables) return "Turns the feature on for more builds.";
  return null;
}

/** The superAdmin rule in words, mirrored on the disabled submit button. */
export function superAdminHint(key: string, riskyDirection: FeatureFlagRiskyDirection): string {
  return riskyDirection === "enable"
    ? `Turning ${key} on for more builds needs the superAdmin role.`
    : `Turning ${key} off for any build needs the superAdmin role.`;
}

// ───────────────────────────── presets ─────────────────────────────

export type TargetingPreset = "disable" | "enable";

const ON: PlatformTargeting = { mode: "on", min: null, max: null };
const OFF: PlatformTargeting = { mode: "off", min: null, max: null };

/** *Disable everywhere (maintenance)* / *Enable everywhere*. */
export function presetTargeting(preset: TargetingPreset): TargetingValues {
  return preset === "disable" ? { ios: OFF, android: OFF } : { ios: ON, android: ON };
}

/** Android side of the review-build preset: always visible, or from the live build up. */
export type ReviewAndroid = { kind: "on" } | { kind: "fromLive"; liveBuild: number };

/**
 * *Review build*: iOS exactly the build in review `[y, y]`; Android `on`, or `[liveBuild, ∞)` so
 * the published app keeps it (Play app-access policy) — see FEATURE_FLAGS_PLAN.md §9.2.
 */
export function reviewBuildTargeting(
  iosReviewBuild: number,
  android: ReviewAndroid,
): TargetingValues {
  return {
    ios: { mode: "builds", min: iosReviewBuild, max: iosReviewBuild },
    android: android.kind === "on" ? ON : { mode: "builds", min: android.liveBuild, max: null },
  };
}

// ───────────────────────────── commands ─────────────────────────────

/** Only the fields that differ from the current row go on the wire (the PATCH body is a subset). */
export function flagPatch(
  baseline: FeatureFlagSnapshot,
  next: FeatureFlagWrite,
): Omit<FeatureFlagUpdateInput, "reason"> {
  const patch: Omit<FeatureFlagUpdateInput, "reason"> = {};
  if (next.iosMode !== baseline.iosMode) patch.iosMode = next.iosMode;
  if (next.iosMinBuild !== baseline.iosMinBuild) patch.iosMinBuild = next.iosMinBuild;
  if (next.iosMaxBuild !== baseline.iosMaxBuild) patch.iosMaxBuild = next.iosMaxBuild;
  if (next.androidMode !== baseline.androidMode) patch.androidMode = next.androidMode;
  if (next.androidMinBuild !== baseline.androidMinBuild) {
    patch.androidMinBuild = next.androidMinBuild;
  }
  if (next.androidMaxBuild !== baseline.androidMaxBuild) {
    patch.androidMaxBuild = next.androidMaxBuild;
  }
  if (next.message !== baseline.message) patch.message = next.message;
  if (!sameInstant(next.expectedBackAt, baseline.expectedBackAt)) {
    patch.expectedBackAt = next.expectedBackAt;
  }
  return patch;
}

/** A create (no row yet: POST, null optionals omitted) or an update (PATCH guarded by `updatedAt`). */
export function flagCommand(
  key: string,
  baseline: FeatureFlagDoc | null,
  next: FeatureFlagWrite,
  reason: string,
): FeatureFlagCommand {
  if (baseline === null) {
    const input: FeatureFlagCreateInput = {
      key,
      iosMode: next.iosMode,
      androidMode: next.androidMode,
      reason,
    };
    if (next.iosMinBuild !== null) input.iosMinBuild = next.iosMinBuild;
    if (next.iosMaxBuild !== null) input.iosMaxBuild = next.iosMaxBuild;
    if (next.androidMinBuild !== null) input.androidMinBuild = next.androidMinBuild;
    if (next.androidMaxBuild !== null) input.androidMaxBuild = next.androidMaxBuild;
    if (next.message !== null) input.message = next.message;
    if (next.expectedBackAt !== null) input.expectedBackAt = next.expectedBackAt;
    return { kind: "create", input };
  }
  return {
    kind: "update",
    id: baseline.id,
    updatedAt: baseline.updatedAt,
    input: { ...flagPatch(baseline, next), reason },
  };
}

export function flagActionError(error: unknown): string {
  return error instanceof Error ? error.message : "The feature flag could not be saved.";
}

// ───────────────────────────── vendor bulk actions ─────────────────────────────

export type VendorAction = "pause" | "resume";

export const VENDOR_ACTION_LABELS: Record<VendorAction, string> = {
  pause: "Pause",
  resume: "Resume",
};

export function vendorActionTitle(action: VendorAction, vendor: FeatureFlagVendor): string {
  return `${VENDOR_ACTION_LABELS[action]} ${VENDOR_LABELS[vendor]}`;
}

/**
 * The values every flag of the vendor is written to: *pause* = off on both platforms with the
 * window's message / expected end; *resume* = on on both platforms, copy cleared.
 */
export function vendorTarget(
  action: VendorAction,
  copy: { message: string | null; expectedBackAt: string | null },
): FeatureFlagWrite {
  return {
    ...toTargeting(presetTargeting(action === "pause" ? "disable" : "enable")),
    ...(action === "pause" ? copy : { message: null, expectedBackAt: null }),
  };
}

export interface VendorPlanItem {
  key: FeatureFlagKey;
  /** The live row, or null when the key still runs on its code default. */
  current: FeatureFlagDoc | null;
  /** What the key evaluates from now (the code-default targeting when it has no row). */
  before: FeatureFlagSnapshot;
  /** The other vendors that also tag this key (e.g. Rain and MeaWallet share `cardPushProvisioning`). */
  sharedWith: FeatureFlagVendor[];
  /**
   * `write` = will be saved; `unchanged` = already there; `superAdminOnly` = skipped for this
   * operator; `shared` = held back: it would switch on a key another vendor may still have paused.
   */
  outcome: "write" | "unchanged" | "superAdminOnly" | "shared";
}

/** The write blanks a message or an expected-back time the row already shows users. */
export function clearsCopy(before: FeatureFlagSnapshot, after: FeatureFlagSnapshot): boolean {
  return (
    (before.message !== null && after.message === null) ||
    (before.expectedBackAt !== null && after.expectedBackAt === null)
  );
}

function snapshotsEqual(a: FeatureFlagSnapshot, b: FeatureFlagSnapshot): boolean {
  return (
    a.iosMode === b.iosMode &&
    a.iosMinBuild === b.iosMinBuild &&
    a.iosMaxBuild === b.iosMaxBuild &&
    a.androidMode === b.androidMode &&
    a.androidMinBuild === b.androidMinBuild &&
    a.androidMaxBuild === b.androidMaxBuild &&
    a.message === b.message &&
    sameInstant(a.expectedBackAt, b.expectedBackAt)
  );
}

/**
 * What a vendor-wide pause / resume does to each key tagged with the vendor, in registry order
 * (Rain: `cardIssuance` first). Keys whose change needs `superAdmin` are skipped for anyone else.
 * A key another vendor also tags is held back from a write that switches it on (a resume) unless
 * `includeShared`: nothing records which vendor paused it, and the other may still be down.
 */
export function planVendorAction<T extends FeatureFlagDoc>(
  vendor: FeatureFlagVendor,
  target: FeatureFlagWrite,
  docs: readonly T[],
  superAdmin: boolean,
  includeShared = false,
): (VendorPlanItem & { current: T | null })[] {
  return keysForVendor(vendor).map((key) => {
    const entry = FEATURE_FLAG_REGISTRY[key];
    const current = liveFlagFor(docs, key);
    const before = currentSnapshot(current, entry.defaultAvailable);
    const sharedWith = entry.vendors.filter((other) => other !== vendor);
    const item = { key, current, before, sharedWith };
    if (snapshotsEqual(before, target)) return { ...item, outcome: "unchanged" as const };
    const change = classifyFeatureFlagChange(before, target);
    if (change.enables && sharedWith.length > 0 && !includeShared) {
      return { ...item, outcome: "shared" as const };
    }
    if (!superAdmin && requiresSuperAdmin(entry.riskyDirection, change)) {
      return { ...item, outcome: "superAdminOnly" as const };
    }
    return { ...item, outcome: "write" as const };
  });
}

/** Vendor names for copy, e.g. "Rain / MeaWallet". */
export function vendorList(vendors: readonly FeatureFlagVendor[]): string {
  return vendors.map((vendor) => VENDOR_LABELS[vendor]).join(" / ");
}
